import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { BusinessServices, ITeam } from '@shs-digi-care-monorepo/interfaces';
import { environment } from '../environments/environment';
import { Paginated } from '../models/paginated.model';
import { UpdateTeam, TeamAssignmentConfiguration, CreateTeam } from './team.model';

/**
 * Service for managing teams via API.
 */
@Injectable({
    providedIn: 'root'
})
export class TeamService {
    private readonly httpClient = inject(HttpClient);
    private readonly baseUrl = `${environment.apiUrl}/teams`;

    /**
     * Fetches teams with pagination and optional business service filter.
     * @param page Page number (default: 0)
     * @param pageSize Items per page (default: 20)
     * @param businessService Optional business service filter
     * @returns Observable of paginated teams
     */
    public getTeams(page = 0, pageSize = 20, businessService?: BusinessServices): Observable<Paginated<ITeam>> {
        return this.httpClient.get<Paginated<ITeam>>(this.baseUrl, {
            params: {
                page,
                pageSize,
                ...((businessService && { businessServices: [businessService] }) || {})
            }
        });
    }

    /**
     * Fetches a single team by ID.
     * @param id The team ID
     * @returns Observable of the team
     */
    public getTeamById(id: string): Observable<ITeam> {
        return this.httpClient.get<ITeam>(`${this.baseUrl}/${id}`);
    }

    /**
     * Updates an existing team.
     * @param id The team ID to update
     * @param data The update data
     * @returns Observable of the updated team
     */
    public updateTeam(id: string, data: UpdateTeam): Observable<ITeam> {
        return this.httpClient.patch<ITeam>(`${this.baseUrl}/${id}`, data);
    }

    /**
     * Updates the request distribution configuration of a single team member.
     * @param teamId The team ID
     * @param userGid The GID of the assigned team member
     * @param data The distribution configuration to apply
     * @param updatedBy The GID of the user performing the change
     * @returns Observable of the updated team
     */
    public updateTeamAssignment(
        teamId: string,
        userGid: string,
        data: TeamAssignmentConfiguration,
        updatedBy: string
    ): Observable<ITeam> {
        return this.httpClient.patch<ITeam>(`${this.baseUrl}/${teamId}/users/${userGid}/assignment`, {
            ...data,
            updatedBy
        });
    }

    /**
     * Creates a new team.
     * @param data The team data
     * @returns Observable of the created team
     */
    public createTeam(data: CreateTeam): Observable<ITeam> {
        return this.httpClient.post<ITeam>(this.baseUrl, data);
    }

    /**
     * Deletes a team by ID.
     * @param id The team ID to delete
     * @returns Observable of void
     */
    public deleteTeam(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${this.baseUrl}/${id}`);
    }
}
